import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FileText, Users, Target, Zap } from 'lucide-react';

const About = () => {
  return (
    <>
      <Helmet>
        <title>About Us - AI Blog</title>
        <meta name="description" content="Learn about our AI-powered blogging platform and our mission to revolutionize content creation." />
      </Helmet>

      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              About AI Blog
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              We're revolutionizing content creation with AI-powered tools that help bloggers 
              create engaging, SEO-optimized content that resonates with readers and search engines.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 mb-16">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Our Mission
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                To democratize content creation by providing bloggers and content creators with 
                powerful AI tools that enhance their creativity while maintaining the human touch 
                that makes content truly engaging.
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                We believe that AI should augment human creativity, not replace it. Our platform 
                helps you generate ideas, optimize content for SEO, and create compelling narratives 
                that connect with your audience.
              </p>
            </div>

            <div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6">
                Our Vision
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                To become the leading platform for AI-assisted content creation, where every blogger 
                can produce high-quality, SEO-optimized content that drives engagement and growth.
              </p>
              <p className="text-gray-600 dark:text-gray-300">
                We envision a future where content creation is more efficient, creative, and accessible 
                to everyone, regardless of their technical expertise or writing experience.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                AI-Powered Content
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Generate high-quality blog posts with our advanced AI technology
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                SEO Optimized
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Every piece of content is optimized for search engines and user engagement
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                User-Friendly
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Intuitive interface designed for bloggers of all skill levels
              </p>
            </div>

            <div className="text-center">
              <div className="bg-primary-100 dark:bg-primary-900 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Zap className="h-8 w-8 text-primary-600 dark:text-primary-400" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Fast & Efficient
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                Create and publish content in minutes, not hours
              </p>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-8">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 text-center">
              Why Choose AI Blog?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  For Content Creators
                </h3>
                <ul className="space-y-3 text-gray-600 dark:text-gray-300">
                  <li className="flex items-start">
                    <span className="text-primary-600 dark:text-primary-400 mr-2">✓</span>
                    Generate unlimited content ideas
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-600 dark:text-primary-400 mr-2">✓</span>
                    Optimize content for SEO automatically
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-600 dark:text-primary-400 mr-2">✓</span>
                    Create engaging titles and meta descriptions
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-600 dark:text-primary-400 mr-2">✓</span>
                    Maintain consistent content quality
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
                  For Blog Owners
                </h3>
                <ul className="space-y-3 text-gray-600 dark:text-gray-300">
                  <li className="flex items-start">
                    <span className="text-primary-600 dark:text-primary-400 mr-2">✓</span>
                    Complete admin dashboard
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-600 dark:text-primary-400 mr-2">✓</span>
                    Real-time analytics and insights
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-600 dark:text-primary-400 mr-2">✓</span>
                    Comment moderation tools
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary-600 dark:text-primary-400 mr-2">✓</span>
                    Google AdSense ready
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default About;
